<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="manager-category-title"
          >
            <b-form-input
              id="manager-category-title"
              v-model="form.title"
              :state="validateState('title')"
              @change="updateAltImage()"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="manager-category-slug"
          >
            <b-form-input
              id="manager-category-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="manager-category-lang"
          >
            <language-selector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="manager-category-keywords"
          >
            <b-form-input
              id="manager-category-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="manager-category-description"
          >
            <b-form-textarea
              id="manager-category-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="managerCatImage"
            v-model="form.managerCatImage"
            :label="$t('general.thumbnail') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('managerCatImage')"
            :alt.sync="form.managerCatImageAlt"
            :alt-validator="$v.form.managerCatImageAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-manager-category-thumbnail-feedback"
                :label="$t('general.thumbnail')"
                :validator="$v.form.managerCatImage"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import languageSelector from '@/components/language-selector.vue'
import { isValidImageSize } from '@core/utils/rules'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import useManagerCategoriesList from './useManagerCategoriesList'

export default {
  name: 'ManagerCategoryAdd',
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    LayoutLoading,
    languageSelector,
  },
  directives: {
    Ripple,
  },
  validations: {
    form: {
      title: { required, minLength: minLength(3) },
      slug: { required },
      keywords: { required, minLength: minLength(3) },
      description: { required, minLength: minLength(10) },
      managerCatImage: {
        required,
        isTrueImage: requiredIf(function () {
          return typeof this.form.managerCatImage === 'object' && !Object.prototype.hasOwnProperty.call(this.form.managerCatImage, '_id')
        }),
        isValidImageSize,
      },
      managerCatImageAlt: { required, minLength: minLength(3) },
    },
  },
  setup() {
    const {
      addItem,
      convertResource,
      errorManager,
      form,
      handleUpload,
      isSubmit,
      itemLoading,
    } = useManagerCategoriesList()

    return {
      addItem,
      convertResource,
      errorManager,
      form,
      handleUpload,
      isSubmit,
      itemLoading,
    }
  },
  watch: {
    'form.title': function (newValue, oldValue) {
      this.$set(this.form, 'slug', this.createSlug(this.form.title))
      if (oldValue === this.form.managerCatImageAlt) {
        this.$set(this.form, 'managerCatImageAlt', newValue)
      }
    },
  },
  created() {
    this.$set(this.form, 'lang', 'fa')
  },
  methods: {
    updateAltImage() {
      if (!this.form.managerCatImageAlt) {
        this.$set(this.form, 'managerCatImageAlt', this.form.title)
      }
    },
    changeSelectedLanguage(selectedLanguage) {
      this.form = this.convertResource(selectedLanguage)
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0]
          .scrollIntoView({ behavior: 'smooth', block: 'center' }), 500)
        return
      }
      const uploadList = ['managerCatImage']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData(form) {
      const data = {
        title: form.title,
        slug: form.slug,
        keywords: form.keywords,
        description: form.description,
        managerCatImageFileId: form.managerCatImage ? form.managerCatImage._id : null,
      }
      this.addItem(data, this.form.lang)
    },
  },
}
</script>
